import { defaultAllSelection } from "services/constants/selections";
import React, { useMemo, useState } from "react";
import * as Mui from "@mui/material";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import PersonOutlineIcon from "@mui/icons-material/PersonOutline";
import { designColors, designShell } from "services/constants/designTokens";

// Shared compact "label - value" dropdown used in the report header-right
// filter row (Department, Report Type, Employee, ...). Extracted out of
// WorkingHours/index.js — where it first shipped as a Working Hours-only
// component — so other single-report screens (e.g. OvertimeReport) opting
// into the same sticky header-filter row don't have to re-implement the
// same ~150 lines locally.
export const CompactHeaderSelectFilter = ({
  dataList = [],
  selectedValue,
  updateDataFunction,
  staticLabel,
  Icons = PersonOutlineIcon,
  widthSx = {},
  buttonSx = {},
  // Opt-in: render each option as avatar + name + employee id, and let the
  // search box match on the employee id too.
  showEmployeeDetails = false,
}) => {
  const [anchorEl, setAnchorEl] = useState(null);
  const [searchValue, setSearchValue] = useState("");
  const normalizedStaticLabel = `${staticLabel || ""}`.trim();
  const selectedLabel =
    typeof selectedValue === "string"
      ? selectedValue
      : selectedValue?.label || selectedValue?.name || "All";
  const filteredOptions = useMemo(() => {
    const query = searchValue.trim().toLowerCase();
    if (!query) return dataList;
    return dataList.filter((option) => {
      const optionLabel =
        typeof option === "string"
          ? option
          : option?.label || option?.name || `${option}`;
      const empId =
        showEmployeeDetails && typeof option === "object"
          ? `${option?.employee_id ?? ""}`.toLowerCase()
          : "";
      return optionLabel.toLowerCase().includes(query) || empId.includes(query);
    });
  }, [dataList, searchValue, showEmployeeDetails]);

  return (
    <Mui.Box sx={{ ...widthSx }}>
      <Mui.ButtonBase
        onClick={(event) => {
          setSearchValue("");
          setAnchorEl(event.currentTarget);
        }}
        sx={{
          ...designShell.compactOutlineButton,
          width: "100%",
          minHeight: 40,
          height: 40,
          justifyContent: "space-between",
          alignItems: "center",
          gap: 1,
          px: 2,
          py: 1,
          borderRadius: 1.5,
          backgroundColor: designColors.slate100,
          border: "none",
          boxShadow: "none",
          color: designColors.slate800,
          "&:hover": {
            backgroundColor: designColors.slate100,
            border: "none",
            boxShadow: "none",
          },
          ...buttonSx,
        }}
      >
        <Mui.Stack
          direction="row"
          spacing={1.1}
          alignItems="center"
          sx={{ minWidth: 0, flex: 1 }}
        >
          <Icons sx={{ fontSize: 18, color: designColors.slate700, flexShrink: 0 }} />
          <Mui.Typography
            sx={{
              minWidth: 0,
              fontSize: 13.5,
              fontWeight: 600,
              color: designColors.slate800,
              whiteSpace: "nowrap",
              overflow: "hidden",
              textOverflow: "ellipsis",
              textAlign: "left",
            }}
          >
            {normalizedStaticLabel
              ? `${normalizedStaticLabel} - ${selectedLabel}`
              : selectedLabel}
          </Mui.Typography>
        </Mui.Stack>
        <KeyboardArrowDownIcon
          sx={{ fontSize: 18, color: designColors.slate700, flexShrink: 0 }}
        />
      </Mui.ButtonBase>
      <Mui.Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={() => {
          setAnchorEl(null);
          setSearchValue("");
        }}
        PaperProps={{
          sx: {
            mt: 1,
            minWidth: 220,
            maxHeight: 320,
            overflowY: "auto",
            borderRadius: "12px",
            border: `1px solid ${designColors.slate200}`,
            boxShadow: "0 8px 24px rgba(15, 23, 42, 0.08)",
          },
        }}
      >
        <Mui.Box
          sx={{
            px: 1,
            pt: 1,
            pb: 0.5,
            position: "sticky",
            top: 0,
            backgroundColor: designColors.white,
            zIndex: 1,
          }}
        >
          <Mui.TextField
            size="small"
            fullWidth
            autoFocus
            placeholder={`Search ${(normalizedStaticLabel || "options").toLowerCase()}`}
            value={searchValue}
            onChange={(event) => setSearchValue(event.target.value)}
            // Menu's MenuList owns a keydown handler for arrow-key
            // navigation and letter-key type-ahead that intercepts every
            // keystroke bubbling up from this field and calls
            // preventDefault() on it — which blocks the browser from ever
            // inserting the character, so typed text silently never
            // appears. Stopping propagation here keeps those keystrokes
            // local to the input.
            onKeyDown={(event) => event.stopPropagation()}
            sx={{
              "& .MuiOutlinedInput-root": {
                borderRadius: "8px",
                backgroundColor: designColors.slate100,
              },
            }}
          />
        </Mui.Box>
        {filteredOptions.map((option, index) => {
          const optionLabel =
            typeof option === "string"
              ? option
              : option?.label || option?.name || `${option}`;
          return (
            <Mui.MenuItem
              key={option?.id || option?.value || optionLabel || index}
              onClick={() => {
                updateDataFunction?.(null, option);
                setAnchorEl(null);
                setSearchValue("");
              }}
              sx={{ fontSize: 13.5 }}
            >
              {showEmployeeDetails && typeof option === "object" ? (
                <Mui.Stack direction="row" spacing={1.25} alignItems="center">
                  <Mui.Avatar
                    src={option?.image_url || undefined}
                    alt={optionLabel?.charAt(0)?.toUpperCase()}
                    sx={{ width: 25, height: 25, fontSize: 12 }}
                  >
                    {optionLabel?.charAt(0)?.toUpperCase()}
                  </Mui.Avatar>
                  <Mui.Box sx={{ lineHeight: 1.25 }}>
                    <Mui.Box component="span" sx={{ display: "block" }}>
                      {optionLabel}
                    </Mui.Box>
                    {option?.employee_id || `${option?.user_id}` === String(defaultAllSelection) ? (
                      <Mui.Box
                        component="span"
                        sx={{ display: "block", fontSize: 12, color: "#6b7280" }}
                      >
                        {option.employee_id || "All"}
                      </Mui.Box>
                    ) : null}
                  </Mui.Box>
                </Mui.Stack>
              ) : (
                optionLabel
              )}
            </Mui.MenuItem>
          );
        })}
        {filteredOptions.length === 0 ? (
          <Mui.MenuItem disabled sx={{ fontSize: 13.5 }}>
            No results found
          </Mui.MenuItem>
        ) : null}
      </Mui.Menu>
    </Mui.Box>
  );
};
